import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { errMsg } from "@/lib/errors";
import InfoTooltip from "@/components/common/InfoTooltip";
import {
  TrendingUp, TrendingDown, Sparkles, Sliders, Activity,
  History as HistoryIcon, CheckCircle2, XCircle, RotateCcw, Calculator,
  AlertTriangle, Info, Plus, Trash2, Star, Wand2, Users2, CalendarClock,
  Bell, Rss, RefreshCw,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

// Etichette italiane e testi di help per i moduli.
const MODULE_LABELS = {
  occupancy: {
    name: "Occupazione",
    help: "Aumenta o diminuisce il prezzo in base a quante camere sono già prenotate per quella data. Più il B&B è pieno, più il sistema alza i prezzi per massimizzare il ricavo.",
  },
  lead_time: {
    name: "Anticipo prenotazione",
    help: "Modifica il prezzo in base a quanti giorni mancano al check-in. Last-minute può essere scontato per riempire; le prenotazioni molto anticipate hanno un piccolo sovrapprezzo.",
  },
  weekend: {
    name: "Weekend",
    help: "Applica un sovrapprezzo o sconto ai giorni del fine settimana (venerdì, sabato, domenica) rispetto agli altri giorni.",
  },
  seasonality: {
    name: "Stagionalità",
    help: "Riutilizza i prezzi di Alta/Media/Bassa stagione che hai già configurato nelle impostazioni stagionali. Non serve reinserirli qui.",
  },
  stay_length: {
    name: "Durata soggiorno",
    help: "Modifica il prezzo in base al numero di notti prenotate. Puoi premiare le prenotazioni lunghe con uno sconto o alzare per soggiorni brevi (1 notte).",
  },
  holidays: {
    name: "Festività",
    help: "Aumenta il prezzo in date festive (Ferragosto, Natale, Pasqua, ecc.) quando la domanda è strutturalmente più alta.",
  },
  competitor: {
    name: "Concorrenti",
    help: "Confronta la tua tariffa con la media dei prezzi dei competitor che hai censito. Se loro vendono più caro, il sistema può alzare il tuo prezzo (Sprint 2).",
  },
  events: {
    name: "Eventi locali",
    help: "Alza il prezzo se in quella data ci sono eventi che attirano pubblico (concerti, sagre, fiere) — Sprint 2.",
  },
  ai_suggestions: {
    name: "Intelligenza Artificiale",
    help: "In futuro l'AI analizzerà lo storico e proporrà nuove regole. Ora disattivato: serve almeno 6 mesi di dati.",
  },
  weather: {
    name: "Meteo",
    help: "In futuro le previsioni meteo influenzeranno il pricing (bel tempo = più richieste turistiche).",
  },
};

const roomLabel = (r) => {
  const n = r?.name ?? r?.room_name;
  if (typeof n === "string") return n;
  if (n && typeof n === "object") return n.it || n.en || Object.values(n)[0] || "Camera";
  return "Camera";
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const fmtEuro = (n) => `€ ${Number(n || 0).toFixed(2)}`;

// ============================================================
//  Componente principale
// ============================================================
export default function RmsTab() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [unreadNotif, setUnreadNotif] = useState(0);
  const [activeTab, setActiveTab] = useState("dashboard");

  const refreshUnread = async () => {
    try {
      const r = await api.get("/admin/rms/notifications/unread-count");
      setUnreadNotif(r.data?.unread || 0);
    } catch { /* noop */ }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/admin/rms/config");
        setConfig(r.data);
      } catch (e) {
        toast.error(errMsg(e));
      } finally {
        setLoading(false);
      }
    })();
    refreshUnread();
    const iv = setInterval(refreshUnread, 45000);
    return () => clearInterval(iv);
  }, []);

  const updateConfig = async (patch) => {
    try {
      const r = await api.put("/admin/rms/config", patch);
      setConfig(r.data);
      toast.success("Impostazioni salvate");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (loading) return <div className="p-6 text-sm text-[#635F59]">Caricamento…</div>;

  return (
    <div className="space-y-4" data-testid="rms-tab">
      {/* Header identico agli altri tab admin */}
      <Card className="p-5 bg-white border border-[#E8E2D9]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#AC5D45] to-[#8A4936] flex items-center justify-center text-white">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-display text-xl text-[#1E3A5F] flex items-center gap-2">
                Revenue Manager
                <InfoTooltip content="Sistema che analizza domanda e occupazione per suggerirti il prezzo migliore. Puoi lasciarlo in modalità solo-suggerimento oppure fargli aggiornare i prezzi automaticamente." />
              </h3>
              <p className="text-xs text-[#635F59] mt-1 max-w-2xl">
                Motore di dynamic pricing modulare. Analizza i tuoi dati per suggerirti prezzi ottimali,
                mantenendoti sempre il controllo. Ogni modulo può essere attivato o disattivato individualmente.
              </p>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={config?.enabled ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-700 border-slate-300"}>
                  {config?.enabled ? "🟢 Motore attivo" : "⚪ Motore spento"}
                </Badge>
                <Badge variant="outline" className="border-[#E8E2D9]">
                  Autonomia:{" "}
                  <b className="ml-1">
                    {config?.autonomy_level === "auto" ? "Automatica" : config?.autonomy_level === "assisted" ? "Assistita" : "Manuale"}
                  </b>
                </Badge>
                <Badge variant="outline" className="border-[#E8E2D9]">
                  Moduli attivi: <b className="ml-1">{(config?.active_modules || []).length}</b>
                </Badge>
                {unreadNotif > 0 && (
                  <Badge
                    className="bg-rose-100 text-rose-800 border-rose-300 cursor-pointer hover:bg-rose-200"
                    onClick={() => setActiveTab("notifications")}
                    data-testid="rms-header-notif-badge"
                    title="Vai alle notifiche"
                  >
                    <Bell className="w-3 h-3 mr-1" /> {unreadNotif} da rivedere
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="border-[#AC5D45] text-[#AC5D45] hover:bg-[#FFF8F0]"
              onClick={() => setWizardOpen(true)}
              data-testid="rms-wizard-open"
            >
              <Wand2 className="w-3.5 h-3.5 mr-1" /> Wizard
            </Button>
            <Label htmlFor="rms-master-switch" className="text-xs text-[#635F59]">
              {config?.enabled ? "Motore ON" : "Motore OFF"}
            </Label>
            <Switch
              id="rms-master-switch"
              checked={!!config?.enabled}
              onCheckedChange={(v) => updateConfig({ enabled: v })}
              data-testid="rms-master-switch"
            />
          </div>
        </div>

        {/* Scorciatoie: dove inserire competitor / eventi / feed automatici.
            Reso visibile perché nel wizard è facile perdersi. */}
        <div className="mt-4 pt-4 border-t border-[#F3EEE7]">
          <div className="text-[10px] uppercase tracking-wider text-[#635F59] mb-2 font-semibold">
            Dati esterni che alzano/abbassano i prezzi ↓
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("config")}
              className="flex items-center gap-2 text-left px-3 py-2 rounded-lg border border-[#E8E2D9] hover:bg-[#FFF8F0] active:scale-[0.98] transition"
              data-testid="rms-shortcut-competitors"
            >
              <Users2 className="w-4 h-4 text-[#AC5D45] shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#1E3A5F]">Concorrenti</div>
                <div className="text-[10px] text-[#635F59] leading-tight">Aggiungi link Booking/Airbnb dei competitor</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("config")}
              className="flex items-center gap-2 text-left px-3 py-2 rounded-lg border border-[#E8E2D9] hover:bg-[#FFF8F0] active:scale-[0.98] transition"
              data-testid="rms-shortcut-events"
            >
              <CalendarClock className="w-4 h-4 text-[#AC5D45] shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#1E3A5F]">Eventi locali</div>
                <div className="text-[10px] text-[#635F59] leading-tight">Concerti, sagre, fiere che aumentano la domanda</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("config")}
              className="flex items-center gap-2 text-left px-3 py-2 rounded-lg border border-[#E8E2D9] hover:bg-[#FFF8F0] active:scale-[0.98] transition"
              data-testid="rms-shortcut-feeds"
            >
              <Rss className="w-4 h-4 text-[#AC5D45] shrink-0" />
              <div className="min-w-0">
                <div className="text-xs font-semibold text-[#1E3A5F]">Feed automatici</div>
                <div className="text-[10px] text-[#635F59] leading-tight">Calendari eventi (Comune, TicketOne, teatri…)</div>
              </div>
            </button>
          </div>
        </div>
      </Card>

      {/* 5 sotto-tab */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-white border border-[#E8E2D9]">
          <TabsTrigger value="dashboard" data-testid="rms-tab-dashboard">
            <Activity className="w-3.5 h-3.5 mr-1.5" /> Dashboard
          </TabsTrigger>
          <TabsTrigger value="notifications" data-testid="rms-tab-notifications" className="relative">
            <Bell className="w-3.5 h-3.5 mr-1.5" /> Notifiche
            {unreadNotif > 0 && (
              <span
                data-testid="rms-notif-badge"
                className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-rose-600 text-white text-[10px] font-bold"
              >
                {unreadNotif > 99 ? "99+" : unreadNotif}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="simulator" data-testid="rms-tab-simulator">
            <Calculator className="w-3.5 h-3.5 mr-1.5" /> Simulatore
          </TabsTrigger>
          <TabsTrigger value="config" data-testid="rms-tab-config">
            <Sliders className="w-3.5 h-3.5 mr-1.5" /> Configurazione
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="rms-tab-history">
            <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> Storico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-4">
          <DashboardPanel />
        </TabsContent>
        <TabsContent value="notifications" className="mt-4">
          <NotificationsPanel onChange={refreshUnread} />
        </TabsContent>
        <TabsContent value="simulator" className="mt-4">
          <SimulatorPanel />
        </TabsContent>
        <TabsContent value="config" className="mt-4">
          <ConfigPanel config={config} onSave={updateConfig} />
        </TabsContent>
        <TabsContent value="history" className="mt-4">
          <HistoryPanel />
        </TabsContent>
      </Tabs>

      <WizardDialog
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        config={config}
        onSaveConfig={updateConfig}
      />
    </div>
  );
}

// ============================================================
//  DASHBOARD
// ============================================================
function DashboardPanel() {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  const load = async (d = date) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/rms/dashboard?check_date=${d}`);
      setData(r.data);
      if (!selectedRoomId && r.data?.rooms?.length) setSelectedRoomId(r.data.rooms[0].room_id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const selected = data?.rooms?.find((r) => r.room_id === selectedRoomId);

  return (
    <div className="space-y-4" data-testid="rms-dashboard-panel">
      <Card className="p-4 flex items-end gap-3 flex-wrap">
        <div>
          <Label className="text-xs">
            <InfoTooltip content="Data per cui vuoi analizzare l'indice di domanda e i prezzi suggeriti per tutte le camere.">Data analisi</InfoTooltip>
          </Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="rms-dashboard-date" />
        </div>
        <Button onClick={() => load(date)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="rms-dashboard-load">
          Aggiorna
        </Button>
        {data?.pending_approvals > 0 && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-300 ml-auto">
            {data.pending_approvals} in attesa di approvazione
          </Badge>
        )}
      </Card>

      {loading && <p className="text-sm text-[#635F59]">Calcolo in corso…</p>}

      {selected && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-[#635F59] mb-2">
              <InfoTooltip content="Numero da 0 a 100 che riassume quanto è 'calda' una data (occupazione, weekend, festività, anticipo). 0-40 domanda bassa, 41-70 media, 71-100 alta.">Indice di domanda</InfoTooltip>
            </div>
            <div className="text-4xl font-display text-[#1E3A5F]" data-testid="rms-demand-index">
              {selected.demand_index.toFixed(0)}<span className="text-lg text-[#635F59]">/100</span>
            </div>
            <div className="mt-2 h-2 bg-[#F3EEE7] rounded-full overflow-hidden">
              <div
                className={`h-full ${selected.demand_index > 70 ? "bg-rose-500" : selected.demand_index > 40 ? "bg-amber-400" : "bg-emerald-500"}`}
                style={{ width: `${selected.demand_index}%` }}
              />
            </div>
            <div className="text-xs text-[#635F59] mt-1">
              {selected.demand_index > 70 ? "Domanda ALTA" : selected.demand_index > 40 ? "Domanda MEDIA" : "Domanda BASSA"}
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><span className="text-[#635F59]">Occupazione:</span> <b>{selected.context.occupancy_pct.toFixed(0)}%</b></div>
              <div><span className="text-[#635F59]">Lead time:</span> <b>{selected.context.lead_time_days} g</b></div>
              <div><span className="text-[#635F59]">Weekend:</span> <b>{[4,5,6].includes(selected.context.day_of_week) ? "sì" : "no"}</b></div>
              <div><span className="text-[#635F59]">Festività:</span> <b>{selected.context.holiday?.name || "—"}</b></div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-xs uppercase tracking-wider text-[#635F59] mb-2">Breakdown prezzo — {roomLabel(selected)}</div>
            <div className="space-y-1 text-sm">
              <Row label="Prezzo base" value={fmtEuro(selected.base_price)} bold />
              {selected.adjustments.map((a, i) => (
                <Row
                  key={`${a.module}-${i}`}
                  label={<span className="inline-flex items-center gap-1">
                    {MODULE_LABELS[a.module]?.name || a.module}
                    <InfoTooltip content={MODULE_LABELS[a.module]?.help || ""} iconClassName="w-3 h-3 text-[#9b948a]" />
                    <span className="text-[10px] text-[#635F59]">· {a.note}</span>
                  </span>}
                  value={<span className={a.delta >= 0 ? "text-emerald-700" : "text-rose-600"}>{a.delta >= 0 ? "+" : ""}{fmtEuro(a.delta)} ({a.pct >= 0 ? "+" : ""}{a.pct}%)</span>}
                />
              ))}
              {selected.clamped_note && (
                <div className="text-xs text-amber-700 flex items-center gap-1 mt-2">
                  <AlertTriangle className="w-3 h-3" /> {selected.clamped_note}
                </div>
              )}
              <div className="border-t border-[#E8E2D9] mt-2 pt-2">
                <Row
                  label={<b>Prezzo suggerito</b>}
                  value={<b className="text-lg text-[#AC5D45]">{fmtEuro(selected.suggested_price)}</b>}
                />
                <div className="text-right text-xs text-[#635F59]">
                  Variazione: {selected.diff_pct >= 0 ? "+" : ""}{selected.diff_pct}%
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {data?.rooms?.length > 0 && (
        <Card className="p-4">
          <div className="text-xs uppercase tracking-wider text-[#635F59] mb-2">Riepilogo camere · {date}</div>

          {/* Mobile: cards impilate con nome completo. Desktop: tabella compatta. */}
          <div className="sm:hidden space-y-2" data-testid="rms-summary-mobile">
            {data.rooms.map((r) => (
              <button
                key={r.room_id}
                type="button"
                onClick={() => setSelectedRoomId(r.room_id)}
                className={`w-full text-left border rounded-lg p-3 transition ${selectedRoomId === r.room_id ? "bg-[#FFF8F0] border-[#AC5D45]" : "border-[#E8E2D9] bg-white hover:bg-[#FFF8F0]"}`}
              >
                <div className="font-semibold text-sm text-[#1E3A5F] break-words leading-tight mb-1.5">
                  {roomLabel(r)}
                </div>
                <div className="grid grid-cols-4 gap-1 text-[11px]">
                  <div>
                    <div className="text-[#635F59] uppercase tracking-wider text-[9px]">Base</div>
                    <div className="font-semibold">{fmtEuro(r.base_price)}</div>
                  </div>
                  <div>
                    <div className="text-[#635F59] uppercase tracking-wider text-[9px]">Suggerito</div>
                    <div className="font-semibold text-[#AC5D45]">{fmtEuro(r.suggested_price)}</div>
                  </div>
                  <div>
                    <div className="text-[#635F59] uppercase tracking-wider text-[9px]">Δ%</div>
                    <div className={`font-semibold ${r.diff_pct >= 0 ? "text-emerald-700" : "text-rose-600"}`}>
                      {r.diff_pct >= 0 ? "+" : ""}{r.diff_pct}%
                    </div>
                  </div>
                  <div>
                    <div className="text-[#635F59] uppercase tracking-wider text-[9px]">Indice</div>
                    <div className="font-semibold">{r.demand_index.toFixed(0)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full text-xs" data-testid="rms-summary-table">
              <thead>
                <tr className="text-left text-[#635F59] border-b border-[#E8E2D9]">
                  <th className="py-2">Camera</th>
                  <th>Base</th>
                  <th>Suggerito</th>
                  <th>Δ%</th>
                  <th>Indice</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.rooms.map((r) => (
                  <tr key={r.room_id} className={`border-b border-[#F3EEE7] ${selectedRoomId === r.room_id ? "bg-[#FFF8F0]" : ""}`}>
                    <td className="py-2 pr-2 min-w-[180px]">{roomLabel(r)}</td>
                    <td>{fmtEuro(r.base_price)}</td>
                    <td className="font-semibold text-[#AC5D45]">{fmtEuro(r.suggested_price)}</td>
                    <td className={r.diff_pct >= 0 ? "text-emerald-700" : "text-rose-600"}>
                      {r.diff_pct >= 0 ? "+" : ""}{r.diff_pct}%
                    </td>
                    <td>{r.demand_index.toFixed(0)}</td>
                    <td className="text-right">
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setSelectedRoomId(r.room_id)}>
                        Vedi
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

const Row = ({ label, value, bold = false }) => (
  <div className="flex items-center justify-between text-xs">
    <span className={bold ? "font-semibold" : ""}>{label}</span>
    <span className={bold ? "font-semibold" : ""}>{value}</span>
  </div>
);

// ============================================================
//  SIMULATORE
// ============================================================
function SimulatorPanel() {
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState("");
  const [date, setDate] = useState(todayIso());
  const [baseOverride, setBaseOverride] = useState("");
  const [ctxOcc, setCtxOcc] = useState("");
  const [ctxStay, setCtxStay] = useState("1");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/admin/rms/rooms/limits");
      setRooms(r.data.rooms || []);
      if (r.data.rooms?.length) setRoomId(r.data.rooms[0].id);
    })();
  }, []);

  const simulate = async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const body = {
        room_id: roomId,
        check_date: date,
        override_context: {
          occupancy_pct: ctxOcc === "" ? undefined : Number(ctxOcc),
          stay_length: Number(ctxStay) || 1,
        },
        override_base: baseOverride === "" ? undefined : Number(baseOverride),
      };
      const r = await api.post("/admin/rms/simulate", body);
      setResult(r.data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4" data-testid="rms-simulator-panel">
      <Card className="p-4">
        <h4 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#AC5D45]" />
          Simulatore prezzo
          <InfoTooltip content="Prova qualsiasi combinazione senza modificare il calendario. Puoi lasciare vuoti i campi di 'contesto' per usare i dati reali della struttura." />
        </h4>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label className="text-xs">Camera *</Label>
            <Select value={roomId} onValueChange={setRoomId}>
              <SelectTrigger data-testid="sim-room"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
              <SelectContent>
                {rooms.map((r) => <SelectItem key={r.id} value={r.id}>{roomLabel(r)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Data *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="sim-date" />
          </div>
          <div>
            <Label className="text-xs">
              <InfoTooltip content="Se lasciato vuoto, useremo il prezzo base della camera. Utile per simulare scenari 'what-if' con prezzi diversi.">Base override (€)</InfoTooltip>
            </Label>
            <Input type="number" step="0.01" placeholder="auto" value={baseOverride} onChange={(e) => setBaseOverride(e.target.value)} data-testid="sim-base" />
          </div>
          <div>
            <Label className="text-xs">
              <InfoTooltip content="Se lasciato vuoto, useremo l'occupazione reale della struttura. Modifica per simulare scenari.">Occupazione (%)</InfoTooltip>
            </Label>
            <Input type="number" step="1" min="0" max="100" placeholder="auto" value={ctxOcc} onChange={(e) => setCtxOcc(e.target.value)} data-testid="sim-occ" />
          </div>
          <div>
            <Label className="text-xs">
              <InfoTooltip content="Numero di notti del soggiorno che vuoi simulare.">Durata soggiorno (notti)</InfoTooltip>
            </Label>
            <Input type="number" step="1" min="1" value={ctxStay} onChange={(e) => setCtxStay(e.target.value)} data-testid="sim-stay" />
          </div>
        </div>
        <div className="mt-4">
          <Button onClick={simulate} disabled={!roomId || loading} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="sim-run">
            {loading ? "Calcolo…" : "▶ Calcola simulazione"}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-4">
          <div className="text-center mb-4">
            <div className="text-xs text-[#635F59] uppercase tracking-wider">Prezzo suggerito</div>
            <div className="text-5xl font-display text-[#AC5D45] my-2" data-testid="sim-result-price">{fmtEuro(result.suggested_price)}</div>
            <div className="text-xs text-[#635F59]">
              da {fmtEuro(result.base_price)} · variazione {result.diff_pct >= 0 ? "+" : ""}{result.diff_pct}%
              · Indice domanda <b>{result.demand_index.toFixed(0)}/100</b>
            </div>
          </div>
          <div className="border-t border-[#E8E2D9] pt-3 space-y-1">
            <Row label="Prezzo base" value={fmtEuro(result.base_price)} bold />
            {result.adjustments.map((a, i) => (
              <Row
                key={`${a.module}-${i}`}
                label={<span>
                  {MODULE_LABELS[a.module]?.name || a.module}
                  <span className="text-[10px] text-[#635F59] ml-1">· {a.note}</span>
                </span>}
                value={<span className={a.delta >= 0 ? "text-emerald-700" : "text-rose-600"}>{a.delta >= 0 ? "+" : ""}{fmtEuro(a.delta)}</span>}
              />
            ))}
            {result.clamped_note && (
              <div className="text-xs text-amber-700 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" /> {result.clamped_note}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// ============================================================
//  CONFIGURAZIONE
// ============================================================
function ConfigPanel({ config, onSave }) {
  const [modules, setModules] = useState([]);
  const [roomLimits, setRoomLimits] = useState([]);

  useEffect(() => {
    (async () => {
      const m = await api.get("/admin/rms/modules");
      setModules(m.data.modules || []);
      const r = await api.get("/admin/rms/rooms/limits");
      setRoomLimits(r.data.rooms || []);
    })();
  }, []);

  const toggleModule = async (id, enabled) => {
    const cur = new Set(config?.active_modules || []);
    if (enabled) cur.add(id); else cur.delete(id);
    await onSave({ active_modules: Array.from(cur) });
    const m = await api.get("/admin/rms/modules");
    setModules(m.data.modules || []);
  };

  const saveRoomLimits = async (rid, min, max) => {
    try {
      await api.post("/admin/rms/rooms/limits", { room_id: rid, min_nightly_price: min ? Number(min) : null, max_nightly_price: max ? Number(max) : null });
      toast.success("Limiti salvati");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="rms-config-panel">
      {/* Generale */}
      <Card className="p-4">
        <h4 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
          Generale
          <InfoTooltip content="Impostazioni di base del motore: attivazione, livello di autonomia, notifiche." />
        </h4>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-xs">
              <InfoTooltip content="Manuale: solo suggerimenti, tu decidi tutto (consigliato per iniziare). Assistita: applica variazioni sotto soglia, chiede permesso per le altre (Sprint 2). Automatica: applica tutto con log e possibilità di rollback.">Livello di autonomia</InfoTooltip>
            </Label>
            <Select value={config?.autonomy_level} onValueChange={(v) => onSave({ autonomy_level: v })}>
              <SelectTrigger data-testid="cfg-autonomy"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manuale (solo suggerimenti)</SelectItem>
                <SelectItem value="assisted">Assistita (soglia)</SelectItem>
                <SelectItem value="auto">Automatica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {config?.autonomy_level === "assisted" && (
            <div>
              <Label className="text-xs">
                <InfoTooltip content="Sotto questa percentuale di variazione, il sistema applica automaticamente. Sopra, ti chiede approvazione. Eventi 'eccezionali' e prezzi vicini ai limiti sono sempre in coda.">
                  Soglia Assistita (±%)
                </InfoTooltip>
              </Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="50"
                defaultValue={config?.assist_threshold_pct ?? 5}
                onBlur={(e) => onSave({ assist_threshold_pct: Number(e.target.value) || 5 })}
                data-testid="cfg-threshold"
              />
            </div>
          )}
        </div>
      </Card>

      {/* Concorrenti */}
      <CompetitorsCard />

      {/* Eventi locali */}
      <EventsCard />

      {/* Sorgenti Feed Eventi (Sprint 3) */}
      <FeedsCard />

      {/* Prezzi min/max per camera */}
      <Card className="p-4">
        <h4 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
          Prezzi minimi e massimi
          <InfoTooltip content="Limiti di sicurezza per camera. Il motore non scenderà mai sotto il minimo né salirà oltre il massimo, anche in caso di domanda estrema." />
        </h4>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#635F59] border-b border-[#E8E2D9]">
                <th className="py-2">Camera</th>
                <th>Base</th>
                <th>Min (€)</th>
                <th>Max (€)</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {roomLimits.map((r, i) => (
                <RoomLimitRow key={r.id} room={r} onSave={saveRoomLimits} idx={i} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Moduli */}
      <Card className="p-4">
        <h4 className="font-display text-lg text-[#1E3A5F] mb-3 flex items-center gap-2">
          Moduli pricing
          <InfoTooltip content="Ogni modulo lavora indipendentemente. Puoi attivare/disattivare i moduli e in futuro configurare le soglie di ognuno." />
        </h4>
        <div className="space-y-2">
          {modules.map((m) => {
            const lbl = MODULE_LABELS[m.id] || { name: m.id, help: "" };
            const isCore = m.available;
            return (
              <div key={m.id} className="flex items-center justify-between border border-[#E8E2D9] rounded-lg px-3 py-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className={`w-2 h-2 rounded-full ${m.enabled ? "bg-emerald-500" : isCore ? "bg-slate-300" : "bg-slate-400 opacity-50"}`} />
                  <div className="min-w-0">
                    <div className="font-medium text-[#1E3A5F] text-sm flex items-center gap-1">
                      {lbl.name}
                      <InfoTooltip content={lbl.help} iconClassName="w-3.5 h-3.5 text-[#9b948a]" />
                      {!isCore && <Badge variant="outline" className="ml-2 text-[10px] py-0 px-1.5 border-slate-300">Sprint 2/futuro</Badge>}
                    </div>
                    <div className="text-[11px] text-[#635F59] truncate">{lbl.help}</div>
                  </div>
                </div>
                <Switch
                  checked={m.enabled}
                  disabled={!isCore}
                  onCheckedChange={(v) => toggleModule(m.id, v)}
                  data-testid={`mod-toggle-${m.id}`}
                />
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function RoomLimitRow({ room, onSave }) {
  const [min, setMin] = useState(room.min_nightly_price ?? "");
  const [max, setMax] = useState(room.max_nightly_price ?? "");
  const suggMin = Math.round((room.price_per_night || 0) * 0.6);
  const suggMax = Math.round((room.price_per_night || 0) * 3);
  return (
    <tr className="border-b border-[#F3EEE7]">
      <td className="py-2">{roomLabel(room)}</td>
      <td>{fmtEuro(room.price_per_night)}</td>
      <td>
        <Input type="number" step="1" min="0" value={min} onChange={(e) => setMin(e.target.value)} placeholder={`≥ ${suggMin}`} className="h-8 w-24" data-testid={`min-${room.id}`} />
      </td>
      <td>
        <Input type="number" step="1" min="0" value={max} onChange={(e) => setMax(e.target.value)} placeholder={`≤ ${suggMax}`} className="h-8 w-24" data-testid={`max-${room.id}`} />
      </td>
      <td className="text-right">
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => onSave(room.id, min, max)}>Salva</Button>
      </td>
    </tr>
  );
}

// ============================================================
//  CONCORRENTI (Sprint 2)
// ============================================================
function CompetitorsCard() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", city: "", type: "similar", url_website: "", url_booking: "", url_airbnb: "" });

  const load = async () => {
    try { const r = await api.get("/admin/rms/competitors"); setItems(r.data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Nome obbligatorio"); return; }
    try {
      await api.post("/admin/rms/competitors", form);
      toast.success("Competitor aggiunto");
      setShowAdd(false);
      setForm({ name: "", city: "", type: "similar", url_website: "", url_booking: "", url_airbnb: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo competitor e tutte le rilevazioni collegate?")) return;
    try { await api.delete(`/admin/rms/competitors/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
          <Users2 className="w-4 h-4 text-[#AC5D45]" />
          Concorrenti
          <InfoTooltip content="Anagrafica delle strutture concorrenti che vuoi monitorare. Le rilevazioni prezzi (inserite manualmente) alimentano il modulo 'Concorrenti' del motore." />
        </h4>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="comp-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#635F59] italic">Nessun competitor censito. Aggiungine almeno 3 per attivare il modulo Concorrenti.</p>
      ) : (
        <div className="space-y-2">
          {items.map((c) => (
            <div key={c.id} className="border border-[#E8E2D9] rounded-lg p-3 flex items-center justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#1E3A5F] text-sm">{c.name} <span className="text-[11px] text-[#635F59]">· {c.city} · {c.type}</span></div>
                <div className="text-[11px] text-[#635F59] mt-0.5 flex gap-2 flex-wrap">
                  {c.url_website && <a href={c.url_website} target="_blank" rel="noreferrer" className="underline">Sito</a>}
                  {c.url_booking && <a href={c.url_booking} target="_blank" rel="noreferrer" className="underline">Booking</a>}
                  {c.url_airbnb && <a href={c.url_airbnb} target="_blank" rel="noreferrer" className="underline">Airbnb</a>}
                </div>
              </div>
              <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700" onClick={() => del(c.id)} data-testid={`comp-del-${c.id}`}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo competitor</DialogTitle>
            <DialogDescription>Anagrafica base. Le rilevazioni prezzi si inseriscono a parte per data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="comp-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Città</Label>
                <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Tipologia</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="similar">Similare</SelectItem>
                    <SelectItem value="superior">Superiore</SelectItem>
                    <SelectItem value="inferior">Inferiore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">URL sito</Label>
              <Input value={form.url_website} onChange={(e) => setForm({ ...form, url_website: e.target.value })} placeholder="https://…" />
            </div>
            <div>
              <Label className="text-xs">URL Booking.com</Label>
              <Input value={form.url_booking} onChange={(e) => setForm({ ...form, url_booking: e.target.value })} placeholder="https://booking.com/…" />
            </div>
            <div>
              <Label className="text-xs">URL Airbnb</Label>
              <Input value={form.url_airbnb} onChange={(e) => setForm({ ...form, url_airbnb: e.target.value })} placeholder="https://airbnb.com/…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annulla</Button>
            <Button className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" onClick={create} data-testid="comp-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
//  EVENTI LOCALI (Sprint 2)
// ============================================================
function EventsCard() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", date: todayIso(), end_date: "", location: "", distance_km: "", category: "", importance: "medium" });

  const load = async () => {
    try { const r = await api.get("/admin/rms/events"); setItems(r.data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.date) { toast.error("Nome e data obbligatori"); return; }
    try {
      const payload = { ...form, distance_km: form.distance_km ? Number(form.distance_km) : null };
      await api.post("/admin/rms/events", payload);
      toast.success("Evento aggiunto");
      setShowAdd(false);
      setForm({ name: "", date: todayIso(), end_date: "", location: "", distance_km: "", category: "", importance: "medium" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;
    try { await api.delete(`/admin/rms/events/${id}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const impLabel = { small: "★ Piccolo", medium: "★★ Medio", big: "★★★ Importante", exceptional: "★★★★ Eccezionale" };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-[#AC5D45]" />
          Eventi locali
          <InfoTooltip content="Eventi (concerti, sagre, fiere) che aumentano la domanda per una data specifica. L'impatto sull'aumento dipende dall'importanza." />
        </h4>
        <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="evt-add">
          <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi
        </Button>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#635F59] italic">Nessun evento registrato. Aggiungi eventi locali per far aumentare i prezzi durante concerti, sagre, fiere.</p>
      ) : (
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {items.map((e) => (
            <div key={e.id} className="border border-[#E8E2D9] rounded p-2 flex items-center justify-between gap-2 text-xs">
              <div className="min-w-0 flex-1">
                <b>{e.name}</b> <span className="text-[#635F59]">· {e.date}{e.end_date ? ` → ${e.end_date}` : ""} · {e.location || "—"}</span>
              </div>
              <Badge variant="outline" className="border-[#E8E2D9]">{impLabel[e.importance] || e.importance}</Badge>
              <Button size="sm" variant="outline" className="h-6 text-[11px] border-rose-300 text-rose-700 px-2" onClick={() => del(e.id)}>
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo evento locale</DialogTitle>
            <DialogDescription>Concerti, sagre, fiere che aumentano la domanda.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome *</Label>
              <Input value={form.name} onChange={(ev) => setForm({ ...form, name: ev.target.value })} data-testid="evt-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Data *</Label>
                <Input type="date" value={form.date} onChange={(ev) => setForm({ ...form, date: ev.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Data fine (opzionale)</Label>
                <Input type="date" value={form.end_date} onChange={(ev) => setForm({ ...form, end_date: ev.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Luogo</Label>
                <Input value={form.location} onChange={(ev) => setForm({ ...form, location: ev.target.value })} placeholder="Piazza Duomo" />
              </div>
              <div>
                <Label className="text-xs">Distanza (km)</Label>
                <Input type="number" step="0.1" value={form.distance_km} onChange={(ev) => setForm({ ...form, distance_km: ev.target.value })} placeholder="0.5" />
              </div>
            </div>
            <div>
              <Label className="text-xs">
                <InfoTooltip content="Piccolo (+5%): sagra locale. Medio (+10%): mostra/fiera. Importante (+20%): concerto/partita. Eccezionale (+35%): grande evento cittadino (finale nazionale, festival internazionale).">
                  Importanza *
                </InfoTooltip>
              </Label>
              <Select value={form.importance} onValueChange={(v) => setForm({ ...form, importance: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">★ Piccolo (+5%)</SelectItem>
                  <SelectItem value="medium">★★ Medio (+10%)</SelectItem>
                  <SelectItem value="big">★★★ Importante (+20%)</SelectItem>
                  <SelectItem value="exceptional">★★★★ Eccezionale (+35%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annulla</Button>
            <Button className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" onClick={create} data-testid="evt-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ============================================================
//  WIZARD INIZIALE (Sprint 2)
// ============================================================
function WizardDialog({ open, onClose, config, onSaveConfig }) {
  const [step, setStep] = useState(1);
  const [rooms, setRooms] = useState([]);
  const [autonomy, setAutonomy] = useState(config?.autonomy_level || "manual");
  const [threshold, setThreshold] = useState(config?.assist_threshold_pct ?? 5);
  const [modulesOn, setModulesOn] = useState({
    occupancy: true, lead_time: true, weekend: true, seasonality: true,
    stay_length: true, holidays: true, competitor: false, events: false,
  });

  useEffect(() => {
    if (!open) return;
    (async () => {
      const r = await api.get("/admin/rms/rooms/limits");
      setRooms(r.data.rooms || []);
    })();
    setStep(1);
  }, [open]);

  const finish = async () => {
    // Apply room limits (defaults suggested if empty)
    for (const r of rooms) {
      const min = r.min_nightly_price || Math.round((r.price_per_night || 0) * 0.6);
      const max = r.max_nightly_price || Math.round((r.price_per_night || 0) * 3);
      try {
        await api.post("/admin/rms/rooms/limits", {
          room_id: r.id, min_nightly_price: min, max_nightly_price: max,
        });
      } catch { /* noop */ }
    }
    // Save config
    const active = Object.keys(modulesOn).filter((k) => modulesOn[k]);
    await onSaveConfig({
      autonomy_level: autonomy,
      assist_threshold_pct: Number(threshold) || 5,
      active_modules: active,
      enabled: true,
    });
    toast.success("Configurazione guidata completata");
    onClose();
  };

  const updateRoomLocal = (idx, field, v) => {
    setRooms((rs) => {
      const copy = [...rs];
      copy[idx] = { ...copy[idx], [field]: v };
      return copy;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Configurazione guidata Revenue Manager · Step {step}/5</DialogTitle>
          <DialogDescription>
            Ti aiuto a configurare il motore in 2 minuti. Puoi cambiare tutto in seguito.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-3 text-sm">
            <p>Benvenuto! Il Revenue Manager analizza domanda, occupazione e stagionalità per suggerirti il prezzo migliore giorno per giorno.</p>
            <p className="text-[#635F59]">Comincia scegliendo <b>limiti di sicurezza</b> per ogni camera, poi le regole predefinite e infine la <b>modalità di autonomia</b>.</p>
            <div className="bg-[#FFF8F0] border border-[#E8E2D9] rounded p-3 text-xs">
              <b>💡 Consiglio:</b> per iniziare, ti suggerisco la modalità <b>Manuale</b> — vedrai i suggerimenti ma nessun prezzo verrà toccato senza il tuo consenso.
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <p className="text-sm">Imposta il prezzo minimo (pavimento) e massimo (soffitto) per camera. Ti suggerisco base×0.6 e base×3.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[#635F59] border-b border-[#E8E2D9]">
                    <th className="py-1">Camera</th>
                    <th>Base</th>
                    <th>Min (€)</th>
                    <th>Max (€)</th>
                  </tr>
                </thead>
                <tbody>
                  {rooms.map((r, i) => (
                    <tr key={r.id} className="border-b border-[#F3EEE7]">
                      <td className="py-1">{roomLabel(r)}</td>
                      <td>{fmtEuro(r.price_per_night)}</td>
                      <td>
                        <Input type="number" className="h-7 w-20" value={r.min_nightly_price ?? Math.round((r.price_per_night || 0) * 0.6)} onChange={(e) => updateRoomLocal(i, "min_nightly_price", Number(e.target.value))} />
                      </td>
                      <td>
                        <Input type="number" className="h-7 w-20" value={r.max_nightly_price ?? Math.round((r.price_per_night || 0) * 3)} onChange={(e) => updateRoomLocal(i, "max_nightly_price", Number(e.target.value))} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-2">
            <p className="text-sm">Applichiamo queste regole standard? Puoi disattivarne alcune se preferisci.</p>
            {Object.keys(modulesOn).map((k) => {
              const l = MODULE_LABELS[k];
              return (
                <label key={k} className="flex items-start gap-2 border border-[#E8E2D9] rounded p-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={modulesOn[k]} onChange={(e) => setModulesOn({ ...modulesOn, [k]: e.target.checked })} className="mt-1" />
                  <div>
                    <div className="font-medium">{l.name}</div>
                    <div className="text-[11px] text-[#635F59]">{l.help}</div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {step === 4 && (
          <div className="space-y-3 text-sm">
            <p>Come vuoi iniziare?</p>
            {[
              { v: "manual", t: "Manuale", d: "Solo suggerimenti, tu decidi tutto (consigliato per iniziare)." },
              { v: "assisted", t: "Assistita", d: "Applica automaticamente le variazioni sotto soglia; per le altre chiede permesso." },
              { v: "auto", t: "Automatica", d: "Applica tutto con log e possibilità di rollback." },
            ].map((o) => (
              <label key={o.v} className={`block border rounded p-3 cursor-pointer ${autonomy === o.v ? "border-[#AC5D45] bg-[#FFF8F0]" : "border-[#E8E2D9]"}`}>
                <input type="radio" name="aut" checked={autonomy === o.v} onChange={() => setAutonomy(o.v)} className="mr-2" />
                <b>{o.t}</b>
                <div className="text-[11px] text-[#635F59] ml-6">{o.d}</div>
              </label>
            ))}
            {autonomy === "assisted" && (
              <div>
                <Label className="text-xs">Soglia (±%)</Label>
                <Input type="number" step="0.5" value={threshold} onChange={(e) => setThreshold(e.target.value)} className="w-24" />
              </div>
            )}
          </div>
        )}

        {step === 5 && (
          <div className="space-y-3 text-sm">
            <p><b>Tutto pronto!</b> Ecco il riepilogo:</p>
            <ul className="text-xs space-y-1 bg-[#FFF8F0] border border-[#E8E2D9] rounded p-3">
              <li>· Camere con limiti: {rooms.length}</li>
              <li>· Moduli attivi: {Object.values(modulesOn).filter(Boolean).length}</li>
              <li>· Autonomia: <b>{autonomy === "auto" ? "Automatica" : autonomy === "assisted" ? `Assistita (soglia ±${threshold}%)` : "Manuale"}</b></li>
              <li>· Il motore verrà <b>attivato</b> alla conferma.</li>
            </ul>
            <p className="text-[11px] text-[#635F59]">Potrai modificare tutto dalla tab Configurazione.</p>
          </div>
        )}

        <DialogFooter>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)}>← Indietro</Button>}
          {step < 5 && <Button className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" onClick={() => setStep(step + 1)}>Continua →</Button>}
          {step === 5 && <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={finish} data-testid="wizard-finish">✓ Termina</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
//  STORICO
// ============================================================
function HistoryPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/rms/history?limit=100");
      setItems(r.data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    try { await api.post("/admin/rms/approve", { history_id: id }); toast.success("Approvato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (id) => {
    try { await api.post("/admin/rms/reject", { history_id: id }); toast.success("Rifiutato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const rollback = async (id) => {
    if (!window.confirm("Annullare questa modifica e ripristinare il prezzo precedente?")) return;
    try { await api.post("/admin/rms/rollback", { history_id: id }); toast.success("Ripristinato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="rms-history-panel">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
            Storico modifiche
            <InfoTooltip content="Ogni modifica prezzi calcolata dal motore. Puoi approvare, rifiutare o annullare (rollback) le variazioni applicate." />
          </h4>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}>Aggiorna</Button>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-[#635F59] italic">Nessuna modifica registrata.</p>
        ) : (
          <div className="space-y-2">
            {items.map((it) => {
              const badgeColor = it.status === "applied" ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : it.status === "pending_approval" ? "bg-amber-100 text-amber-800 border-amber-300"
                : it.status === "rejected" ? "bg-rose-100 text-rose-800 border-rose-300"
                : "bg-slate-100 text-slate-700 border-slate-300";
              return (
                <div key={it.id} className="border border-[#E8E2D9] rounded-lg p-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <b className="text-sm">{roomLabel(it)} · {it.date}</b>
                        <Badge className={badgeColor}>{it.status}</Badge>
                        {it.rolled_back && <Badge variant="outline" className="border-slate-300">rollback</Badge>}
                      </div>
                      <div className="text-xs text-[#635F59] mt-1">
                        {fmtEuro(it.old_price)} → <b className="text-[#AC5D45]">{fmtEuro(it.new_price)}</b>
                        <span className={it.diff_pct >= 0 ? " text-emerald-700" : " text-rose-600"}>
                          {" "}({it.diff_pct >= 0 ? "+" : ""}{it.diff_pct}%)
                        </span>
                        <span className="ml-2">· Indice {Math.round(it.demand_index)}/100</span>
                      </div>
                      <div className="text-[11px] text-[#635F59] mt-1">
                        Moduli: {(it.modules_applied || []).map(m => MODULE_LABELS[m]?.name || m).join(", ") || "—"}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {it.status === "pending_approval" && !it.rolled_back && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]" onClick={() => approve(it.id)} data-testid={`approve-${it.id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approva
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700" onClick={() => reject(it.id)} data-testid={`reject-${it.id}`}>
                            <XCircle className="w-3 h-3 mr-1" /> Rifiuta
                          </Button>
                        </>
                      )}
                      {it.status === "applied" && !it.rolled_back && (
                        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => rollback(it.id)} data-testid={`rollback-${it.id}`}>
                          <RotateCcw className="w-3 h-3 mr-1" /> Annulla
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}


// ============================================================
//  NOTIFICHE IN-APP (Sprint 3)
// ============================================================
function NotificationsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyUnread, setOnlyUnread] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/rms/notifications?only_unread=${onlyUnread}&limit=100`);
      setItems(r.data.items || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [onlyUnread]);

  const markRead = async (id) => {
    try { await api.post("/admin/rms/notifications/read", { id }); load(); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const markAllRead = async () => {
    try {
      const r = await api.post("/admin/rms/notifications/mark-all-read");
      toast.success(`Segnate come lette: ${r.data.updated}`);
      load(); onChange && onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/admin/rms/notifications/${id}`); load(); onChange && onChange(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const approve = async (n) => {
    if (!n.history_id) { markRead(n.id); return; }
    try {
      await api.post("/admin/rms/approve", { history_id: n.history_id });
      toast.success("Prezzo approvato e applicato");
      load(); onChange && onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (n) => {
    if (!n.history_id) { markRead(n.id); return; }
    try {
      await api.post("/admin/rms/reject", { history_id: n.history_id });
      toast.success("Proposta rifiutata");
      load(); onChange && onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const sevBadge = (s) =>
    s === "action" ? "bg-rose-100 text-rose-800 border-rose-300"
    : s === "warning" ? "bg-amber-100 text-amber-800 border-amber-300"
    : "bg-sky-100 text-sky-800 border-sky-300";

  return (
    <div data-testid="rms-notifications-panel">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
            <Bell className="w-4 h-4 text-[#AC5D45]" />
            Notifiche Revenue Manager
            <InfoTooltip content="Qui trovi tutte le proposte di variazione prezzo (Modalità Manuale e Assistita) e gli alert del motore. Il pallino rosso sul menu 'Revenue Manager' indica quante sono da rivedere." />
          </h4>
          <div className="flex items-center gap-2">
            <label className="text-xs flex items-center gap-1 text-[#635F59]">
              <input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} data-testid="notif-only-unread" />
              Solo non lette
            </label>
            <Button size="sm" variant="outline" onClick={markAllRead} data-testid="notif-mark-all">
              Segna tutte lette
            </Button>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
            </Button>
          </div>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-8 text-sm text-[#635F59]">
            <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p>Nessuna notifica {onlyUnread ? "non letta" : ""}. Tutto tranquillo!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((n) => (
              <div
                key={n.id}
                className={`border rounded-lg p-3 ${n.read ? "border-[#E8E2D9] bg-white opacity-70" : "border-[#AC5D45] bg-[#FFF8F0]"}`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <b className="text-sm text-[#1E3A5F]">{n.title}</b>
                      <Badge className={sevBadge(n.severity)} variant="outline">
                        {n.severity === "action" ? "Azione richiesta" : n.severity === "warning" ? "Attenzione" : "Info"}
                      </Badge>
                      {n.read && <Badge variant="outline" className="text-[10px]">Letta</Badge>}
                    </div>
                    <div className="text-xs text-[#635F59] mt-1">{n.message}</div>
                    <div className="text-[10px] text-[#a09489] mt-1">
                      {new Date(n.created_at).toLocaleString("it-IT")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-wrap">
                    {!n.read && n.kind === "price_approval" && n.history_id && (
                      <>
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-[11px]" onClick={() => approve(n)} data-testid={`notif-approve-${n.id}`}>
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Approva
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700" onClick={() => reject(n)} data-testid={`notif-reject-${n.id}`}>
                          <XCircle className="w-3 h-3 mr-1" /> Rifiuta
                        </Button>
                      </>
                    )}
                    {!n.read && (
                      <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => markRead(n.id)} data-testid={`notif-read-${n.id}`}>
                        Segna letta
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 text-[11px] text-[#635F59]" onClick={() => del(n.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}


// ============================================================
//  SORGENTI FEED EVENTI (Sprint 3): iCal / RSS / JSON automatici
// ============================================================
function FeedsCard() {
  const [items, setItems] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [refreshing, setRefreshing] = useState(null);
  const [form, setForm] = useState({
    name: "", url: "", feed_type: "ical",
    default_importance: "medium", default_location: "", active: true,
  });

  const load = async () => {
    try { const r = await api.get("/admin/rms/event-feeds"); setItems(r.data.items || []); }
    catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim() || !form.url.trim()) { toast.error("Nome e URL obbligatori"); return; }
    try {
      await api.post("/admin/rms/event-feeds", form);
      toast.success("Feed aggiunto");
      setShowAdd(false);
      setForm({ name: "", url: "", feed_type: "ical", default_importance: "medium", default_location: "", active: true });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo feed? Verranno rimossi anche gli eventi auto-importati da questa fonte.")) return;
    try { await api.delete(`/admin/rms/event-feeds/${id}`); toast.success("Eliminato"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const toggleActive = async (feed) => {
    try {
      await api.patch(`/admin/rms/event-feeds/${feed.id}`, { ...feed, active: !feed.active });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const refresh = async (id) => {
    setRefreshing(id);
    try {
      const r = await api.post(`/admin/rms/event-feeds/${id}/refresh`);
      if (r.data?.ok) {
        toast.success(`Importati ${r.data.imported} nuovi eventi (${r.data.total_found} trovati totali)`);
      } else {
        toast.error(`Errore feed: ${r.data?.error || "sconosciuto"}`);
      }
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRefreshing(null); }
  };

  const refreshAll = async () => {
    setRefreshing("all");
    try {
      const r = await api.post("/admin/rms/event-feeds/refresh-all");
      const total = (r.data?.results || []).reduce((a, x) => a + (x.imported || 0), 0);
      toast.success(`Aggiornati ${r.data.feeds} feed · ${total} nuovi eventi importati`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setRefreshing(null); }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h4 className="font-display text-lg text-[#1E3A5F] flex items-center gap-2">
          <Rss className="w-4 h-4 text-[#AC5D45]" />
          Sorgenti Feed Eventi (automatiche)
          <InfoTooltip content="URL pubblici (iCal, RSS, JSON) da cui il sistema scarica gli eventi in automatico ogni notte. Es: calendario Comune di Reggio Calabria, TicketOne, Stadio Granillo, teatri. Gli eventi importati arricchiscono la sezione 'Eventi locali' e alzano i prezzi nelle date importanti senza richiederti di inserirli a mano." />
        </h4>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={refreshAll} disabled={refreshing === "all"}>
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${refreshing === "all" ? "animate-spin" : ""}`} />
            Aggiorna tutti
          </Button>
          <Button size="sm" onClick={() => setShowAdd(true)} className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" data-testid="feed-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi feed
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-[#635F59] italic space-y-2">
          <p>Nessun feed configurato. Aggiungi sorgenti pubbliche per popolare la sezione Eventi automaticamente.</p>
          <div className="bg-[#FFF8F0] border border-[#E8E2D9] rounded p-3 text-xs not-italic space-y-1 text-[#1E3A5F]">
            <div className="font-semibold">Suggerimenti per Reggio Calabria:</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Google Calendar pubblico eventi comunali (formato .ics)</li>
              <li>RSS feed teatri / eventi turistici Calabria</li>
              <li>JSON feed proprietario del Comune (se disponibile)</li>
            </ul>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((f) => (
            <div key={f.id} className="border border-[#E8E2D9] rounded-lg p-3 flex items-start justify-between gap-2 flex-wrap" data-testid={`feed-${f.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#1E3A5F] text-sm flex items-center gap-2 flex-wrap">
                  {f.name}
                  <Badge variant="outline" className="text-[10px]">{f.feed_type.toUpperCase()}</Badge>
                  {!f.active && <Badge variant="outline" className="text-[10px] border-slate-300 bg-slate-50">Disattivo</Badge>}
                </div>
                <a href={f.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#635F59] underline break-all">{f.url}</a>
                <div className="text-[11px] text-[#635F59] mt-1">
                  {f.last_fetch_at
                    ? <>Ultimo aggiornamento: {new Date(f.last_fetch_at).toLocaleString("it-IT")} · <b>{f.last_fetch_count || 0}</b> eventi · {f.last_fetch_status === "ok" ? "✓ OK" : <span className="text-rose-600">{f.last_fetch_status}</span>}</>
                    : <>Mai aggiornato</>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => refresh(f.id)} disabled={refreshing === f.id} data-testid={`feed-refresh-${f.id}`}>
                  <RefreshCw className={`w-3 h-3 mr-1 ${refreshing === f.id ? "animate-spin" : ""}`} />
                  Aggiorna
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => toggleActive(f)}>
                  {f.active ? "Disattiva" : "Attiva"}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] border-rose-300 text-rose-700 px-2" onClick={() => del(f.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo feed eventi</DialogTitle>
            <DialogDescription>
              Aggiungi un URL pubblico (iCal, RSS o JSON). Il sistema scaricherà gli eventi ogni notte alle 04:00.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Nome del feed *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Comune di Reggio Calabria" data-testid="feed-form-name" />
            </div>
            <div>
              <Label className="text-xs">URL *</Label>
              <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://…" data-testid="feed-form-url" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">
                  <InfoTooltip content="iCal (.ics) = Google Calendar pubblici, Outlook, calendari eventi. RSS = news/feed teatri/portali. JSON = feed proprietari (array di oggetti con name/date).">Tipo feed *</InfoTooltip>
                </Label>
                <Select value={form.feed_type} onValueChange={(v) => setForm({ ...form, feed_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ical">iCal (.ics)</SelectItem>
                    <SelectItem value="rss">RSS / Atom</SelectItem>
                    <SelectItem value="json">JSON (custom)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  <InfoTooltip content="Importanza di default per eventi non classificati da parole chiave (es. 'concerto' → automatico big). Puoi correggere singoli eventi manualmente dopo l'import.">Importanza default</InfoTooltip>
                </Label>
                <Select value={form.default_importance} onValueChange={(v) => setForm({ ...form, default_importance: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">★ Piccolo</SelectItem>
                    <SelectItem value="medium">★★ Medio</SelectItem>
                    <SelectItem value="big">★★★ Importante</SelectItem>
                    <SelectItem value="exceptional">★★★★ Eccezionale</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Luogo default</Label>
              <Input value={form.default_location} onChange={(e) => setForm({ ...form, default_location: e.target.value })} placeholder="es. Reggio Calabria centro" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Annulla</Button>
            <Button className="bg-[#AC5D45] hover:bg-[#8A4936] text-white" onClick={create} data-testid="feed-form-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}